import { ComputedRef, InjectionKey } from 'vue';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { ArrayElement } from '@/types/utils';
import { useWorkloadsTableConstants } from '@/views/workloads/workloads.const';
import { useWorkloadsPersistentDataProvider, WorkloadsPagePersistentDataType } from '@/views/workloads/workloads.helpers';

export type TableEditorGetter<T extends OsType> = T extends OsType.Windows
  ? ComputedRef<WorkloadsPagePersistentDataType['tableEditor']['windows']>
  : T extends OsType.Linux
  ? ComputedRef<WorkloadsPagePersistentDataType['tableEditor']['linux']>
  : never;

export type TableEditorSetter<T extends OsType> = T extends OsType.Windows
  ? (value: WorkloadsPagePersistentDataType['tableEditor']['windows']) => void
  : T extends OsType.Linux
  ? (value: WorkloadsPagePersistentDataType['tableEditor']['linux']) => void
  : never;

export type TableEditor = {
  get: {
    [OsType.Windows]: TableEditorGetter<OsType.Windows>;
    [OsType.Linux]: TableEditorGetter<OsType.Linux>;
    [OsType.Mac]: TableEditorGetter<OsType.Linux>;
  };
  set: {
    [OsType.Windows]: TableEditorSetter<OsType.Windows>;
    [OsType.Linux]: TableEditorSetter<OsType.Linux>;
    [OsType.Mac]: TableEditorSetter<OsType.Linux>;
  };
};

export type columnOrder = ArrayElement<ReturnType<typeof useWorkloadsTableConstants>['columns']['value']>;

export type WorkloadsTableWindowsColumns =
  | 'Status'
  | 'Build Title'
  | 'Command Line'
  | 'Initiator Name'
  | 'Build Group'
  | 'Initiator Type'
  | 'Build Type'
  | 'Start Time'
  | 'Duration'
  | 'Failure Reason'
  | 'Total Tasks'
  | 'Local Tasks'
  | 'Cloud Tasks'
  | 'Remote Tasks'
  | 'Max. Helper Cores'
  | 'Avg. Helper Cores'
  | 'Max. Needed Helper Cores'
  | 'Hit'
  | 'Miss'
  | 'Uncacheable Tasks';

export type WorkloadsTableLinuxColumns =
  | 'Status'
  | 'Build Caption'
  | 'Command Line'
  | 'Initiator Name'
  | 'Build Group'
  | 'Start Time'
  | 'Duration'
  | 'Failure Reason'
  | 'Total Tasks'
  | 'Local Tasks'
  | 'Cloud Tasks'
  | 'Remote Tasks'
  | 'Max. Helper Cores'
  | 'Avg. Helper Cores'
  | 'Max. Needed Helper Cores';

export type WorkloadsTableMacColumns =
  | 'Status'
  | 'Build Caption'
  | 'Command Line'
  | 'Initiator Name'
  | 'Build Group'
  | 'Start Time'
  | 'Duration'
  | 'Failure Reason'
  | 'Total Tasks'
  | 'Local Tasks'
  | 'Remote Tasks'
  | 'Max. Helper Cores'
  | 'Avg. Helper Cores'
  | 'Max. Needed Helper Cores';

export type WorkloadsTableColumns = WorkloadsTableWindowsColumns | WorkloadsTableLinuxColumns | WorkloadsTableMacColumns;

export type TableColumn = {
  name: WorkloadsTableColumns;
  width: number;
  minWidth?: number;
  sortable: true;
};

export type TableColumns = Array<{
  name: WorkloadsTableColumns;
  width: number;
  minWidth?: number;
  sortable: true;
}>;

export type ColumnsFilter = Partial<Record<WorkloadsTableWindowsColumns, boolean>> & Partial<Record<WorkloadsTableLinuxColumns, boolean>>;

export const IKWorkloadsPersistentData = Symbol('IKWorkloadsPersistentData') as InjectionKey<ReturnType<typeof useWorkloadsPersistentDataProvider>>;
